import { forwardRef } from 'react';

import { Card } from '~/components/ui/card';
import { cn } from '~/lib/utils';

type AccentPosition = 'top' | 'left' | 'none';
type GlowEffect = 'none' | 'subtle' | 'highlight';

interface BrandCardProps extends React.ComponentProps<'div'> {
  /** Position of the accent bar */
  accent?: AccentPosition;
  /** Glow effect intensity */
  glow?: GlowEffect;
  /** Whether the card is interactive (clickable) */
  interactive?: boolean;
  /** Children content */
  children: React.ReactNode;
}

/**
 * Accent styling.
 *
 * `top` renders a 3px primary gradient bar across the top edge as a
 * background-image layer over the card's `bg-card` color. A background is
 * clipped by the card's own border-radius, so the bar follows the rounded
 * corners without overflow clipping (which would cut off the glow halo).
 * `left` renders a 4px solid primary border on the left edge.
 * The gradient uses the `--primary` token.
 */
const accentClasses: Record<AccentPosition, string> = {
  none: '',
  top: cn(
    'bg-[linear-gradient(90deg,var(--primary)_0%,transparent_100%)]',
    'bg-size-[100%_3px] bg-top bg-no-repeat'
  ),
  left: 'border-l-4 border-l-primary',
};

/**
 * Glow styling.
 *
 * Renders a blurred gradient halo behind the card via an ::after pseudo-element.
 * `subtle` reveals on hover; `highlight` pulses continuously using the
 * `glow-pulse` keyframes injected inline below.
 */
const glowClasses: Record<GlowEffect, string> = {
  none: '',
  subtle: cn(
    'relative overflow-visible',
    "after:absolute after:-inset-px after:-z-[1] after:rounded-[inherit] after:content-['']",
    'after:bg-[linear-gradient(135deg,rgba(217,93,11,0.2),rgba(240,122,40,0.1))]',
    'after:opacity-0 after:blur-[12px] after:transition-opacity after:duration-300',
    'hover:after:opacity-100'
  ),
  highlight: cn(
    'relative overflow-visible',
    "after:absolute after:-inset-px after:-z-[1] after:rounded-[inherit] after:content-['']",
    'after:bg-[linear-gradient(135deg,rgba(217,93,11,0.4),rgba(240,122,40,0.2))]',
    'after:opacity-[0.15] after:blur-[16px]',
    'after:animate-[glow-pulse_4s_ease-in-out_infinite]'
  ),
};

const interactiveClasses = cn(
  'cursor-pointer transition-[border-color,transform,box-shadow] duration-150 ease-out',
  'hover:-translate-y-0.5 hover:border-primary hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]',
  'active:translate-y-0'
);

const leftAccentInteractiveClasses = 'hover:translate-x-1 hover:-translate-y-0.5';

/**
 * Brand-styled card built on the shadcn Card primitive.
 *
 * Adds optional accent bars, hover/animated glow halos, and interactive lift
 * behavior. Styling lives entirely in Tailwind utilities plus the shadcn CSS
 * variable tokens (`--primary`, etc.).
 */
export const BrandCard = forwardRef<HTMLDivElement, BrandCardProps>(
  ({ accent = 'none', glow = 'none', interactive = false, className, children, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        className={cn(
          'overflow-hidden border-border',
          accentClasses[accent],
          glowClasses[glow],
          interactive && interactiveClasses,
          interactive && accent === 'left' && leftAccentInteractiveClasses,
          className
        )}
        {...props}
      >
        {glow === 'highlight' && (
          <style>{`@keyframes glow-pulse {
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.25; }
}`}</style>
        )}
        {children}
      </Card>
    );
  }
);

BrandCard.displayName = 'BrandCard';
